/* eslint import/prefer-default-export: "off" */
import { includes } from 'lodash'
import { match, generatePath } from 'react-router-dom'
import type { Location } from 'history'
import type { CloudTypeEnum } from '@src/endpoints/services/base.service'
import type { ResourceTypeEnum } from '@src/endpoints/services/commonHdr.service'
import { ParseCamURIResp, getURIParam } from './url.utils'

export type PageParams<T> = T & {
  page: string
  accountId: string
}
export type WithScope<T> = T & {
  scopeUri?: string
}

export type UrlParams = WithScope<
  PageParams<{
    tenantId: string
    cloudType: CloudTypeEnum
    accountId: string
    region: string
    resourceType?: ResourceTypeEnum
    type: ResourceTypeEnum
    id: string
  }>
>

export const getUriFromParams = (
  params: ParseCamURIResp & {
    resourceType?: ResourceTypeEnum
  } = {} as ParseCamURIResp & { resourceType?: ResourceTypeEnum }
) => {
  if (Object.keys(params).length < 1) {
    return ''
  }
  const { tenantId, cloudType, accountId, region, type, resourceType, id } =
    params
  return decodeURIComponent(
    `${tenantId}:${cloudType}:${accountId}:${getURIParam(region)}:${
      resourceType || type
    }:${id}`
  )
}

export const getDecode = <T extends string>(value: T): T =>
  typeof value === 'string' ? (decodeURIComponent(value) as T) : (value as T)

export const generateUrl = <T = UrlParams>(
  pattern: string,
  params?: { [paramName in keyof T]: T[keyof T] }
) => generatePath(pattern, { ...(params as any) })

export interface Props<Params extends { [K in keyof Params]?: string } = {}> {
  match?: match<Params>
}

export interface PageByPathNameOptions<T> {
  location: Location
}

export const isPageByPathName = <T>(
  name: string,
  options: PageByPathNameOptions<T>
) => {
  const { location = {} as Location } = (options ||
    {}) as PageByPathNameOptions<T>
  const { pathname } = location
  return includes(pathname, name)
}

export const isPageByPathNameFromLocation = <T>(
  name: string,
  location: Location
) => {
  const { pathname } = location || {}
  return includes(pathname, name)
}

type MatchParamKeyBy<T, K extends keyof T> = T extends UrlParams ? K | 'uri' : K
type WithUriParam<P> = P & { uri?: string }
type WithUriParamBy<P, K extends keyof P> = WithUriParam<P>[K]
type MatchParamResultBy<T, K extends keyof T> = T extends UrlParams
  ? WithUriParamBy<T, K>
  : T[K]

export const getMatchParamByKey = <T, K extends keyof T>(
  {
    match: { params = {} as T & { uri?: string } } = {} as match<
      T & { uri?: string }
    >
  }: Readonly<Props<T & { uri?: string }>>,
  key: MatchParamKeyBy<T, K>
): MatchParamResultBy<T, K> => {
  params.uri = getUriFromParams(
    params as ParseCamURIResp & { resourceType?: ResourceTypeEnum }
  )
  return getDecode<any>(params[key]) as MatchParamResultBy<T, K>
}

type MatchParamsResult<T> = T extends UrlParams ? WithUriParam<T> : T

export const getMatchParams = <T>({
  match: { params = {} as T & { uri?: string } } = {} as match<
    T & { uri?: string }
  >
}: Readonly<Props<T & { uri?: string }>> &
  Readonly<{
    children?: React.ReactNode
  }>): MatchParamsResult<T> => {
  for (const key in params) {
    params[key] = getDecode<any>(params[key])
  }
  return {
    ...params,
    uri: getUriFromParams(
      params as ParseCamURIResp & { resourceType?: ResourceTypeEnum }
    )
  } as MatchParamsResult<T>
}
